import React from "react";
import clsx from "clsx";
import styles from "./ComponentCard.module.css";
import type { SharedCardRenderProps } from "./ComponentCard.types.ts";
import StockStatusDisplay from "./StockStatusDisplay.tsx";
import ValidationBadges from "./ValidationBadges.tsx";
import { PriceDifferenceBadge } from "./PriceDifferenceBadge.tsx";
import { ExclamationMarkTriangle } from "../common/icons";
import { useStrings } from "../../context/ConfiguratorStringsContext.ts";

export interface SingleSelectionCardContentProps extends SharedCardRenderProps {
	isInDrawer: boolean;
	imageUrl?: string;
	isEmptyProduct: boolean;
	displayTitle?: string;
	displayDescription?: string;
	displayMeta: string[];
	stockStatus?: { iconClass?: string; description?: string };
	materialNumber?: string;
	priceDifference?: { net: number; gross: number };
	isButtonDisabled: boolean;
	buttonText: string | undefined;
}

const SingleSelectionCardContent: React.FC<SingleSelectionCardContentProps> = ({
	kind,
	isCustomized,
	isReplaced,
	validationStatus,
	validationErrors,
	children,
	productGroupId,
	onSelectClick,
	onReadMoreClick,
	isInDrawer,
	imageUrl,
	isEmptyProduct,
	displayTitle,
	displayDescription,
	displayMeta,
	stockStatus,
	materialNumber,
	priceDifference,
	isButtonDisabled,
	buttonText,
}) => {
	const strings = useStrings();

	return (
		<>
			<div
				className={clsx(styles.imageContainer, {
					[styles.imageContainerInDrawer]: isInDrawer,
				})}
			>
				{isEmptyProduct ? (
					<div
						className={clsx(styles.thumbnail, styles.emptyThumbnail)}
						role="img"
						aria-label={displayTitle}
					>
						<span className={styles.emptyIcon} aria-hidden="true">+</span>
					</div>
				) : (
					<img
						src={imageUrl}
						alt={displayTitle ?? ""}
						className={styles.thumbnail}
					/>
				)}
			</div>
			<div className={styles.content}>
				<div className={styles.header}>
					<div className={styles.titleSection}>
						<div className={styles.kind}>
							{isInDrawer ? displayTitle : kind}
							{isCustomized && !isEmptyProduct && (
								<span className={styles.customizedBadge}>
									{" "}
									• {strings.product.configBundles.configured}
								</span>
							)}
						</div>
						<h3 className={styles.title}>
							{isInDrawer ? displayDescription : displayTitle}
						</h3>
					</div>
					<div className={styles.headerRight}>
						{isInDrawer && priceDifference && (
							<PriceDifferenceBadge priceDifference={priceDifference} />
						)}
						<div className={styles.metaList}>
							{displayMeta.map((item, index) => (
								<span key={index} className={styles.metaBadge}>
									{item}
								</span>
							))}
						</div>
					</div>
				</div>
				{children}
				<div>
					{stockStatus && (
						<div className={styles.leftFooterSection}>
							<StockStatusDisplay stockStatus={stockStatus} />
							{isInDrawer && (
								<span className={styles.partNumber}>
									{`${strings.product.configBundles?.partNumber}: ${materialNumber}`}
								</span>
							)}
						</div>
					)}
				</div>
				<div className={styles.footer}>
					{isReplaced && (
						<span className={clsx(styles.validationBadge, styles.warning)}>
							<ExclamationMarkTriangle aria-hidden="true" />
							{strings.product.configBundles.componentReplaced}
						</span>
					)}
					{!isEmptyProduct ? (
						<>
							{validationStatus && (
								<ValidationBadges
									validationStatus={validationStatus}
									validationErrors={validationErrors}
								/>
							)}
							<button
								className={styles.linkButton}
								onClick={onReadMoreClick}
								aria-label={`${strings.product.configBundles.readMore}: ${displayTitle}`}
								data-testid={
									!productGroupId
										? `component-card-product-${materialNumber}-read-more-button`
										: `component-card-group-${productGroupId}-read-more-button`
								}
							>
								{strings.product.configBundles.readMore}
							</button>
						</>
					) : !isReplaced ? (
						<div className={styles.footerSpacer} />
					) : null}
					<button
						className={styles.selectButton}
						onClick={onSelectClick}
						disabled={isButtonDisabled}
						aria-label={displayTitle ? `${buttonText}: ${displayTitle}` : buttonText}
						data-testid={
							!productGroupId
								? `component-card-product-${materialNumber}-edit-button`
								: `component-card-group-${productGroupId}-edit-button`
						}
					>
						{buttonText}
					</button>
				</div>
			</div>
		</>
	);
};

export default SingleSelectionCardContent;